import { inject, ref, watch, watchEffect } from 'vue'
import { zlibSync, unzlibSync, strToU8, strFromU8 } from 'fflate'
import { VuetifyStore } from '../repl/VuetifyStore'

export function utoa (data: string): string {
  const buffer = strToU8(data)
  const zipped = zlibSync(buffer, { level: 9 })
  const binary = strFromU8(zipped, true)
  return btoa(binary)
}

export function atou (base64: string): string {
  const binary = atob(base64)

  // zlib header (x78), level 9 (xDA)
  if (binary.startsWith('\x78\xDA')) {
    const buffer = strToU8(binary, true)
    const unzipped = unzlibSync(buffer)
    return strFromU8(unzipped)
  }

  return decodeURIComponent(escape(binary))
}

export function useStore () {
  const versions = inject('versions') as Record<'vue' | 'vuetify' | 'vuetifyNightly', string[]>

  const ssrHash = location.hash.includes('__SSR__')
  const hash = atou(location.hash.slice(ssrHash ? 8 : 1))
  const [files, hashVueVersion, hashVuetifyVersion] = (hash ? JSON.parse(hash) : []) as [Record<string, string>, string, string] | []

  const vueVersion = ref(hashVueVersion ?? versions.vue[0])
  const vuetifyVersion = ref(hashVuetifyVersion ?? versions.vuetify[0])
  const ssr = ref(ssrHash)
  const nightly = ref(/-\d{8}\.\d$/.test(vuetifyVersion.value))

  const store = new VuetifyStore({
    defaultFiles: files,
    defaultVueVersion: vueVersion.value,
    defaultVuetifyVersion: vuetifyVersion.value
  })

  watch(vueVersion, value => store.setVueVersion(value))
  watch(vuetifyVersion, value => store.setVuetifyVersion(value))

  watchEffect(() => {
    const files = store.getFiles()

    const hash = utoa(JSON.stringify([files, vueVersion.value, vuetifyVersion.value]))

    history.replaceState({}, '', `#${ssr.value ? '__SSR__' : ''}${hash}`)
  })

  return { store, vueVersion, vuetifyVersion, ssr, nightly }
}

export type State = ReturnType<typeof useStore>
